// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 08-app-deployment.md#kill-switch
// The one pause confirm. Shared by the app-detail power menu and the inactivity
// banner above it, so "pause this app" has a single implementation and a single
// piece of copy however the owner arrives at it.
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { refreshAfterLifecycleChange } from './lifecycleQueries';

export interface PauseAppDialogProps {
  appId: string;
  appName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Disables the confirm while the caller has another lifecycle request in flight. */
  busy?: boolean;
}

/**
 * Pausing is an immediate scale-to-zero with a visible outage, so it is always
 * behind a confirm — including from the banner, where the owner may be one
 * mis-click away from taking down an app somebody else still uses.
 */
export function PauseAppDialog({
  appId,
  appName,
  open,
  onOpenChange,
  busy = false,
}: PauseAppDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const mutation = api.apps.update.useMutation();
  const [pausing, setPausing] = React.useState(false);

  const pause = async (): Promise<void> => {
    if (pausing) return;
    setPausing(true);
    try {
      const res = await mutation.mutateAsync({
        params: { id: appId },
        body: { status: 'suspended', reason: 'Paused via the IT dashboard' },
      });
      if (res.status >= 300) throw res;
      toast.success(`${appName} paused.`);
      onOpenChange(false);
      await refreshAfterLifecycleChange(queryClient, appId);
    } catch (err) {
      toast.error(`Failed: ${mutationErrorMessage(err)}`);
    } finally {
      setPausing(false);
    }
  };

  return (
    <GLDrawer
      variant="confirm"
      mobileBreakpoint={768}
      open={open}
      onOpenChange={onOpenChange}
      preventDismiss={pausing}
      title={`Pause ${appName}?`}
      description={`This immediately scales ${appName} to zero and revokes its workload credentials. Users will see an outage until it is restored. This action is audited.`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pausing || busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void pause()} disabled={pausing || busy}>
            {pausing ? 'Pausing…' : `Pause ${appName}`}
          </Button>
        </>
      }
    >
      {null}
    </GLDrawer>
  );
}
